import {
  setOnboardingSettled,
  useGetCurrentUserQuery,
  useGetOnboardingStateQuery,
  useRefreshOnboardingOnMutations,
} from "@/shared/services";
import { useIsOnboardingCohort } from "@features/onboarding/cohort";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  hydratePlatformChecklist,
  markPlatformTaskDone,
  usePlatformChecklistStore,
} from "./checklist-store";
import { reportCompletedTasks } from "./report-completed-tasks";
import { usePlatformUiStore } from "./ui-store";
import { useVariantOnboarding } from "./use-onboarding-variant";

export const usePlatformChecklistTrackers = () => {
  const { data: me } = useGetCurrentUserQuery();
  const isCohort = useIsOnboardingCohort(me?.createdAt);
  const { data: state } = useGetOnboardingStateQuery({ enabled: isCohort });
  const userId = me?._id;
  const { pathname } = useLocation();
  const hydratedUserId = usePlatformChecklistStore((s) => s.userId);
  const completed = usePlatformChecklistStore((s) => s.completed);
  const sidebarDismissed = usePlatformUiStore((s) => !!s.sidebarDismissed);
  const { isReady, tasks, taskSet } = useVariantOnboarding();
  // Nothing left to tick, or the user hid the progress card: stop refreshing on every page and save.
  const isSettled =
    sidebarDismissed ||
    (isReady && !!state && tasks.every((task) => completed.includes(task.key)));
  useRefreshOnboardingOnMutations(isCohort && !isSettled);

  useEffect(() => {
    setOnboardingSettled(isSettled && userId ? userId : null);
  }, [isSettled, userId]);

  useEffect(() => {
    if (!userId) return;
    // The sidebar is the only surface mounted on every platform route, and the
    // sidebar card can be dismissed from any route, so hydrate here.
    usePlatformUiStore.getState().hydrate(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId || !state) return;
    hydratePlatformChecklist(userId, Object.keys(state.tasks));
  }, [userId, state]);

  useEffect(() => {
    if (!userId || !state || !isReady || !isCohort) return;
    reportCompletedTasks({
      userId,
      serverTasks: state.tasks,
      tasks,
      taskSet,
      userCreatedAt: me?.createdAt,
    });
  }, [userId, state, isReady, isCohort, tasks, taskSet, me?.createdAt]);

  // Opening the analytics page is the spec's client-side signal; the server
  // also derives it from `user.firstAnalytics`, whichever lands first.
  useEffect(() => {
    if (!hydratedUserId || !isCohort) return;
    if (pathname.startsWith("/pages/analytics")) {
      markPlatformTaskDone("view-first-analytics");
    }
  }, [pathname, hydratedUserId, isCohort]);
};
